import { useContext } from 'react'
import {
  ActionDependant,
  evaluateTemplateValue,
  GlobalDataContext,
  TemplateContext,
  View,
  useEvaluatedAttributes,
} from '@ea-lab/reactive-json'
import { useRowContext } from '../editable/useRowContext'
import { resolveRichTextHtml } from '../editable/richTextHtml'

/**
 * ReactiveJSON primitive — an editable CELL of a list row, edited in place.
 *
 * Authors never write this type by hand: `expandEditable` rewrites any node
 * whose `editable:` carries a `column:` into a `BuilderRowField`, e.g.
 *
 *   - type: span
 *     editable: { field: stats, column: label, type: text }
 *     content: ~.label
 *
 * A plain `editable:` can't be used inside a Switch template because the
 * row index is only known at render time. This primitive reads its own
 * store path to emit the edit-runtime markers with the row baked in:
 *
 *   data-builder-field    = top-level field owning the array (`stats`)
 *   data-builder-row-path = cell path relative to it (`0.label`, `1.destinations.2.city`)
 *   data-builder-type     = editor type (`text`, `textarea`, `link`, `image-drupal`…)
 *   data-builder-options  = JSON of the remaining editor options
 *
 * `BuilderBlockWrapper` then edits the cell exactly like a top-level field
 * (inline contentEditable for text, anchored popover otherwise) and commits
 * the WHOLE array back through the unit — same write path as before, only
 * the entry point moved from a modal to the rendered cell.
 *
 * Nested lists use the dotted `field:` form: inside a `destinations` row of a
 * `regions` row, `editable: { field: regions.destinations, column: city }`.
 *
 * A cell whose carrier was a `BuilderHtmlField` (a rich-text column) is
 * flagged `html: true` by `expandEditable`: its content is then injected as
 * HTML instead of rendered as rjBuild children, so a rich-text column reads
 * in the row exactly as the whole-field renderer would show it. The inline
 * TipTap editor mounts inside this very element, so the edited surface keeps
 * the row's own typography.
 */
export const BuilderRowField = ({ props, path, datafield, currentData }) => {
  const { topField, cellPath } = useRowContext(props, path, datafield)
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const evaluatedAttrs = useEvaluatedAttributes(props.attributes ?? {})
  const { style: userStyle, className, ...restAttrs } = evaluatedAttrs
  const Tag = props.tag ?? 'span'
  const html = props.html === true
    ? resolveRichTextHtml(evaluateTemplateValue({
      valueToEvaluate: props.content,
      globalDataContext,
      templateContext,
    }))
    : null

  const options = props.options ?? {}
  const hasOptions = Object.keys(options).length > 0
  const classes = ['builder-editable-field', className].filter(Boolean).join(' ')

  // A cell renders EITHER injected HTML or rjBuild children — React refuses
  // both on the same element, so the two are never spread together.
  const cellAttrs = {
    ...restAttrs,
    className: classes,
    style: userStyle,
    'data-builder-field': cellPath != null ? topField : undefined,
    'data-builder-row-path': cellPath ?? undefined,
    'data-builder-type': props.editorType,
    'data-builder-options': hasOptions ? JSON.stringify(options) : undefined,
    'data-placeholder': options.placeholder ?? options.label ?? undefined,
  }

  // `ActionDependant` so a cell keeps the node's own `actions:` (hide, setData…)
  // — `expandEditable` rewrites the node type, it must not swallow its behaviour.
  return (
    <ActionDependant {...props}>
      {html != null ? (
        <Tag {...cellAttrs} dangerouslySetInnerHTML={{ __html: html }} />
      ) : (
        <Tag {...cellAttrs}>
          {props.content != null && (
            <View
              props={props.content}
              path={path + '.content'}
              datafield="content"
              currentData={currentData?.content}
            />
          )}
        </Tag>
      )}
    </ActionDependant>
  )
}
